import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface TranslationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: number | null;
  isTranslating: boolean;
  onTranslate: (targetLanguage: string) => void;
}

const SUPPORTED_LANGUAGES = [
  { value: "Spanish", label: "Spanish" },
  { value: "Chinese", label: "Chinese" },
  { value: "Tagalog", label: "Tagalog" },
  { value: "Vietnamese", label: "Vietnamese" },
  { value: "Arabic", label: "Arabic" },
  { value: "French", label: "French" },
  { value: "Korean", label: "Korean" },
  { value: "Russian", label: "Russian" },
  { value: "German", label: "German" },
  { value: "Marshallese", label: "Marshallese" },
];

export default function TranslationDialog({
  open,
  onOpenChange,
  messageId,
  isTranslating,
  onTranslate,
}: TranslationDialogProps) {
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");

  // Reset selected language when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedLanguage("");
    }
  }, [open]);

  const handleTranslate = () => {
    if (selectedLanguage) {
      onTranslate(selectedLanguage);
    }
  };

  const handleCancel = () => {
    setSelectedLanguage("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Translate This Response</DialogTitle>
          <DialogDescription>
            Select a language to translate the response into.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
            <SelectTrigger>
              <SelectValue placeholder="Select a language" />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED_LANGUAGES.map((language) => (
                <SelectItem key={language.value} value={language.value}>
                  {language.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isTranslating}>
            Cancel
          </Button>
          <Button onClick={handleTranslate} disabled={!selectedLanguage || isTranslating}>
            {isTranslating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Translating...
              </>
            ) : (
              "Translate"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
